// Cave84 — Saved Views service (F8.2)
// Firestore path: users/{userId}/saved_views/{viewId}
//
// Each saved view stores a named filter set + sort + view mode so the Owner
// can switch between pre-configured inventory perspectives instantly.

import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { auth, db } from './firebase';
import type { SavedView, InventoryViewMode } from '@cave84/shared-types';
import type { InventoryFilters } from '@/hooks/useInventory';

// ─── helpers ─────────────────────────────────────────────────────────────────

function savedViewsCol(userId: string) {
  return collection(db, 'users', userId, 'saved_views');
}

function requireUser(): string {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Not authenticated');
  return uid;
}

// ─── reads ───────────────────────────────────────────────────────────────────

export async function listSavedViews(): Promise<SavedView[]> {
  const uid  = requireUser();
  const snap = await getDocs(
    query(savedViewsCol(uid), orderBy('created_at', 'asc'))
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as SavedView));
}

// ─── writes ──────────────────────────────────────────────────────────────────

export interface SaveViewPayload {
  name: string;
  filters: InventoryFilters;
  sortBy: InventoryFilters['sortBy'];
  viewMode: InventoryViewMode;
}

/** Create a new saved view. Returns the new document ID. */
export async function createSavedView(payload: SaveViewPayload): Promise<string> {
  const uid    = requireUser();
  const newRef = doc(savedViewsCol(uid));
  const now    = serverTimestamp() as unknown as Timestamp;

  await setDoc(newRef, {
    name:       payload.name,
    filters:    payload.filters as unknown as Record<string, unknown>,
    sort_by:    payload.sortBy,
    view_mode:  payload.viewMode,
    created_at: now,
    updated_at: now,
  });

  return newRef.id;
}

/** Overwrite an existing saved view with the current filter state. */
export async function updateSavedView(
  viewId: string,
  payload: SaveViewPayload
): Promise<void> {
  const uid = requireUser();
  await updateDoc(doc(savedViewsCol(uid), viewId), {
    name:       payload.name,
    filters:    payload.filters as unknown as Record<string, unknown>,
    sort_by:    payload.sortBy,
    view_mode:  payload.viewMode,
    updated_at: serverTimestamp(),
  });
}

/** Delete a saved view. */
export async function deleteSavedView(viewId: string): Promise<void> {
  const uid = requireUser();
  await deleteDoc(doc(savedViewsCol(uid), viewId));
}
